import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {FaInfoCircle} from "react-icons/fa";
import {useDebugMode} from "../hooks/useDebugMode";

const formatScore = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value >= 1 ? value.toFixed(2) : value.toFixed(4);
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const getRuleLabel = (rule) => {
  const name = rule.rule_name || rule.name || `Rule ${rule.rule_id || rule.id}`;
  const type = rule.rule_type || rule.type || "rule";
  return `${name} (${type})`;
};

const RecommendationWhyBadge = ({
  explanation,
  source,
  className = "",
  align = "right",
}) => {
  const debugMode = useDebugMode();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const closeTimerRef = useRef(null);

  const details = useMemo(() => {
    const safe = explanation && typeof explanation === "object" ? explanation : {};
    const score = safe.score || {};
    const stages = asArray(safe.pipeline?.stages || safe.stages);
    const rules = asArray(safe.rules || safe.applied_rules || safe.appliedRules);
    const experiments = asArray(safe.experiments);
    const shownBecause = asArray(
      safe.shown_because || safe.shownBecause || safe.reasons
    );

    return {
      summary:
        safe.summary ||
        (source ? `Included by ${source}.` : "Recommendation explanation"),
      source: safe.source || source || "",
      rank: safe.rank || safe.position || null,
      score,
      shownBecause,
      stages,
      rules,
      experiments,
      contextId: safe.context?.context_id || safe.contextId || null,
      userSegments: asArray(safe.user_segments || safe.userSegments),
    };
  }, [explanation, source]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current || typeof window === "undefined") return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 32);
    const preferredLeft =
      align === "right"
        ? rect.right - width
        : align === "center"
          ? rect.left + rect.width / 2 - width / 2
          : rect.left;
    const left = Math.max(
      16,
      Math.min(preferredLeft, window.innerWidth - width - 16)
    );
    const top = Math.min(rect.bottom + 8, window.innerHeight - 336);
    setPosition({
      left,
      top: Math.max(16, top),
      width,
    });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  if (!debugMode) return null;
  if (!explanation && !source) return null;

  const finalScore = formatScore(details.score.final);
  const retrievalScore = formatScore(details.score.retrieval);
  const rankerScore = formatScore(details.score.ranker);
  const canUsePortal = typeof document !== "undefined";

  const popover = (
    <div
      className="overflow-hidden rounded-md border border-pencil/30 bg-popover text-left font-sans text-meta text-ink shadow-overlay"
      style={
        position
          ? {
              left: position.left,
              position: "fixed",
              top: position.top,
              width: position.width,
              zIndex: 9999,
            }
          : undefined
      }
      onClick={(event) => event.stopPropagation()}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <div className="max-h-80 overflow-y-auto p-3">
        <p className="font-semibold leading-snug text-ink">{details.summary}</p>

        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-kicker uppercase tracking-[0.06em] text-ink-muted">
          {details.source && <span>Source: {details.source}</span>}
          {details.rank && <span>Rank: {details.rank}</span>}
          {details.contextId && <span>Context: {details.contextId}</span>}
          {finalScore && <span>Final: {finalScore}</span>}
          {retrievalScore && <span>Retrieval: {retrievalScore}</span>}
          {rankerScore && <span>Ranker: {rankerScore}</span>}
        </div>

        {details.shownBecause.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 font-semibold">Why shown</p>
            <ul className="space-y-1">
              {details.shownBecause.map((reason, index) => (
                <li key={`${reason}-${index}`} className="leading-snug">
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {details.experiments.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 font-semibold">A/B tests</p>
            <ul className="space-y-1">
              {details.experiments.map((experiment) => (
                <li
                  key={`${experiment.experiment_id}-${experiment.variant_id}`}
                  className="leading-snug"
                >
                  {experiment.experiment_name || "Experiment"}:{" "}
                  {experiment.variant_name || experiment.variant_id}
                </li>
              ))}
            </ul>
          </div>
        )}

        {details.rules.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 font-semibold">Rules considered</p>
            <ul className="space-y-1">
              {details.rules.map((rule, index) => (
                <li
                  key={`${rule.rule_id || rule.id || index}-${index}`}
                  className="leading-snug"
                >
                  <span
                    className={
                      rule.applied
                        ? "font-semibold text-emerald-600 dark:text-emerald-300"
                        : rule.matched
                          ? "font-semibold text-amber-600 dark:text-amber-300"
                          : "text-gray-500 dark:text-gray-400"
                    }
                  >
                    {rule.applied
                      ? "Applied"
                      : rule.matched
                        ? "Matched"
                        : "No match"}
                  </span>
                  {": "}
                  {getRuleLabel(rule)}
                  {rule.effect ? ` - ${rule.effect}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {details.stages.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 font-semibold">Pipeline</p>
            <ul className="space-y-1">
              {details.stages.map((stage, index) => {
                const itemEvents = asArray(stage.item_events);
                return (
                  <li key={`${stage.stage || index}-${index}`}>
                    <span className="font-medium">
                      {stage.stage || "stage"}
                    </span>
                    {stage.status ? `: ${stage.status}` : ""}
                    {itemEvents.length > 0 && (
                      <span className="block text-ink-tertiary">
                        {itemEvents
                          .map((event) =>
                            `${event.type || "event"} ${event.effect || ""}`.trim()
                          )
                          .join("; ")}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {details.userSegments.length > 0 && (
          <p className="mt-3 text-kicker text-ink-muted">
            User segments: {details.userSegments.join(", ")}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`inline-flex h-6 items-center gap-1 rounded-sm bg-pencil px-1.5 font-sans text-kicker font-semibold uppercase tracking-[0.09em] text-pencil-foreground shadow-raised transition-[transform,filter] duration-150 ease-out hover:brightness-110 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-pencil focus-visible:ring-offset-2 ${className}`}
        aria-expanded={open}
        aria-label="Why was this recommended?"
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          cancelClose();
          setOpen(true);
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            scheduleClose();
          }
        }}
        onClick={(event) => {
          event.stopPropagation();
          cancelClose();
          setOpen((prev) => !prev);
        }}
      >
        <FaInfoCircle className="h-3 w-3" aria-hidden="true" />
        <span>Why</span>
      </button>

      {open && canUsePortal && position && createPortal(popover, document.body)}
    </>
  );
};

export default RecommendationWhyBadge;
